import styled, { css, createGlobalStyle } from 'styled-components'
import { COLORS } from 'theme'
import { FONT } from 'theme/font'

/* ============= TWO-PANEL LAYOUT ============= */
export const FormBuilderContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: 560px;
  border: 1px solid ${COLORS.GREY_T_90};
  border-radius: 10px;
  overflow: hidden;
  background: ${COLORS.WHITE};
`

export const LeftPanel = styled.div`
  width: 290px;
  min-width: 290px;
  border-right: 1px solid ${COLORS.GREY_T_90};
  display: flex;
  flex-direction: column;
  background: ${COLORS.WHITE};
  overflow-y: auto;
  max-height: 680px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const RightPanel = styled.div`
  flex: 1;
  background: #F4F5F7;
  padding: 0px 18px 0px 18px;
  overflow-y: auto;
  max-height: 680px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

/* ============= LEFT PANEL - STATUS LEGEND ============= */
export const StatusLegendBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  border-bottom: 1px solid ${COLORS.GREY_T_90};
`

export const StatusLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 12px;
  font-weight: ${FONT.FW_500};
  color: ${COLORS.GREY_T_40};
`

export const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${props => {
    switch (props.status) {
      case 'required': return '#F87171'
      case 'optional': return '#60A5FA'
      case 'off': return '#D1D5DB'
      default: return '#D1D5DB'
    }
  }};
`

/* ============= LEFT PANEL - NOTES ============= */
export const NotesBox = styled.div`
  margin: 10px 12px;
  padding: 10px 12px;
  background: ${COLORS.BLUE_T_97};
  border: 1px solid ${COLORS.BLUE_T_90};
  border-radius: 8px;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 11px;
  line-height: 17px;
  color: ${COLORS.GREY_T_30};
`

export const NotesTitle = styled.div`
  font-weight: ${FONT.FW_700};
  margin-bottom: 2px;
  color: ${COLORS.GREY_T_20};
  font-size: 12px;
`

export const NotesLink = styled.span`
  color: ${COLORS.PRIMARY};
  cursor: pointer;
  text-decoration: underline;
  font-weight: ${FONT.FW_500};
`

/* ============= LEFT PANEL - SECTIONS ============= */
export const SectionBlock = styled.div`
  border-bottom: 1px solid ${COLORS.GREY_T_90};
`

export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_700};
  font-size: 13px;
  color: ${COLORS.GREY_T_20};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${COLORS.GREY_T_96};
  }

  svg {
    transition: transform 0.2s;
    ${props => props.collapsed && css`transform: rotate(-90deg);`}
  }
`

/* ============= LEFT PANEL - FIELD ITEMS ============= */
export const FieldItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 8px 7px 12px;
  border-bottom: 1px solid ${COLORS.GREY_T_96};
  transition: background 0.12s;

  &:hover {
    background: ${COLORS.GREY_T_96};
  }
`

export const DragHandle = styled.div`
  display: grid;
  grid-template-columns: 3px 3px;
  gap: 2px;
  cursor: grab;
  padding: 4px 2px;
  opacity: 0.35;

  &:hover {
    opacity: 0.7;
  }

  span {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: ${COLORS.GREY_T_40};
  }
`

export const FieldName = styled.span`
  flex: 1;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 12px;
  font-weight: ${FONT.FW_500};
  color: ${COLORS.GREY_T_20};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

export const StatusBadgeGroup = styled.div`
  display: flex;
  gap: 3px;
  align-items: center;
  flex-shrink: 0;
`

export const StatusBadge = styled.div`
  padding: 1px 6px;
  border-radius: 3px;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 10px;
  font-weight: ${FONT.FW_700};
  cursor: pointer;
  transition: all 0.12s;
  user-select: none;
  line-height: 18px;

  ${props => {
    if (props.disabled) {
      return css`
        cursor: not-allowed;
        opacity: 0.3;
        color: ${COLORS.GREY_T_70};
        &:hover { background: transparent; }
      `
    }
    
    if (props.active) {
      switch (props.badgeType) {
        case 'required':
          return css`background: #FEE2E2; color: #DC2626;`
        case 'optional':
          return css`background: #DBEAFE; color: #2563EB;`
        case 'off':
          return css`background: #E5E7EB; color: #6B7280;`
        default:
          return ''
      }
    }
    return css`
      background: transparent;
      color: ${COLORS.GREY_T_60};
      &:hover { background: ${COLORS.GREY_T_95}; }
    `
  }}
`

export const MoreButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  cursor: pointer;
  color: ${COLORS.GREY_T_60};
  font-size: 14px;
  flex-shrink: 0;

  &:hover {
    background: ${COLORS.GREY_T_95};
  }
`

/* ============= RIGHT PANEL - PREVIEW ============= */
export const PreviewCard = styled.div`
  background: ${COLORS.WHITE};
  border-radius: 10px;
  border: 1px solid ${COLORS.GREY_T_90};
  padding: 28px 32px;
  max-width: 660px;
  margin: 0 auto;
`

export const FormTitleInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 22px;
  font-weight: ${FONT.FW_700};
  color: ${COLORS.GREY_T_10};
  padding: 6px 0;
  border-bottom: 2px solid transparent;
  margin-bottom: 4px;

  &:focus {
    border-bottom-color: ${COLORS.PRIMARY};
  }

  &::placeholder {
    color: ${COLORS.GREY_T_60};
  }
`

export const PreviewSectionWrapper = styled.div`
  margin-top: 22px;
`

export const PreviewSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`

export const PreviewSectionTitle = styled.span`
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_700};
  font-size: 15px;
  color: ${COLORS.GREY_T_10};
`

export const ClearButton = styled.span`
  font-family: ${FONT.FONT_FAMILY};
  font-size: 13px;
  font-weight: ${FONT.FW_500};
  color: ${COLORS.PRIMARY};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const PreviewFieldWrapper = styled.div`
  margin-bottom: 18px;
`

export const PreviewFieldLabel = styled.label`
  display: block;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 12px;
  font-weight: ${FONT.FW_500};
  color: ${COLORS.GREY_T_40};
  margin-bottom: 5px;

  ${props => props.isRequired && css`
    &::after {
      content: ' *';
      color: #DC2626;
    }
  `}
`

export const PreviewInput = styled.div`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${COLORS.GREY_T_80};
  border-radius: 6px;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 13px;
  color: ${COLORS.GREY_T_60};
  background: ${COLORS.WHITE};
  min-height: 38px;
`

export const PreviewSelect = styled.div`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${COLORS.GREY_T_80};
  border-radius: 6px;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 13px;
  color: ${COLORS.GREY_T_60};
  background: ${COLORS.WHITE};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 38px;

  &::after {
    content: '\\25BE';
    color: ${COLORS.GREY_T_60};
    font-size: 12px;
  }
`

export const PreviewRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const PreviewRadioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 13px;
  color: ${COLORS.GREY_T_30};
`

export const PreviewRadio = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${COLORS.GREY_T_80};
  border-radius: ${props => props.type === 'checkbox' ? '3px' : '50%'};
  flex-shrink: 0;
`

/* ============= CUSTOM QUESTIONS ============= */
export const CustomQuestionsWrapper = styled.div`
  margin-top: 20px;
  border-top: 1px solid ${COLORS.GREY_T_90};
  padding-top: 16px;
`

export const CustomQuestionsTitle = styled.div`
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_700};
  font-size: 13px;
  color: ${COLORS.GREY_T_20};
  margin-bottom: 12px;
`

export const AddCustomButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  cursor: pointer;
  font-family: ${FONT.FONT_FAMILY};
  font-size: 13px;
  font-weight: ${FONT.FW_600};
  color: ${COLORS.PRIMARY};
  margin-top: 8px;

  ${props => props.disabled && css`
    cursor: not-allowed !important;
    opacity: 0.4;
  `}

  &:hover {
    opacity: ${props => props.disabled ? 0.4 : 0.8};
  }
`

/* ============= QUESTION CARD (custom questions) ============= */
export const QuestionCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid ${COLORS.GREY_T_90};
  border-radius: 10px;
  background: ${COLORS.WHITE};
  margin-bottom: 12px;
`

export const QuestionCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`

export const QuestionNumber = styled.span`
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_600};
  font-size: ${FONT.F_SM};
  color: ${COLORS.GREY_T_30};
  min-width: 22px;
`

export const QuestionInput = styled.textarea`
  width: 100%;
  min-height: 40px;
  padding: 10px 14px;
  border: 1px solid ${COLORS.GREY_T_80};
  border-radius: 8px;
  box-sizing: border-box;
  line-height: 20px;
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_400};
  font-size: ${FONT.F_SM};
  color: ${COLORS.GREY_T_20};
  outline: none;
  resize: none;
  overflow: hidden;
  &:focus {
    border-color: ${COLORS.PRIMARY};
  }
  &::placeholder {
    color: ${COLORS.GREY_T_60};
  }
`

export const ValidationMessage = styled.div`
  font-family: ${FONT.FONT_FAMILY};
  font-size: ${FONT.F_XS};
  color: #DC2626;
  font-weight: ${FONT.FW_400};
`

export const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

export const OptionInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${COLORS.GREY_T_80};
  border-radius: 6px;
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_400};
  font-size: ${FONT.F_XS};
  color: ${COLORS.GREY_T_20};
  outline: none;

  &:focus { border-color: ${COLORS.PRIMARY}; }
  &::placeholder { color: ${COLORS.GREY_T_60}; }
`

export const IconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;

  ${props => props.disabled && css`
    cursor: not-allowed !important;
    opacity: 0.4;
    &:hover { background: transparent; }
  `}

  &:hover { background: ${COLORS.GREY_T_95}; }
`

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px solid ${COLORS.GREY_T_90};
  margin-top: 10px;
`

export const CardActionsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const RequiredToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const RequiredLabel = styled.span`
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_500};
  font-size: ${FONT.F_SM};
  color: ${COLORS.GREY_T_30};
`

export const AddOptionButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-family: ${FONT.FONT_FAMILY};
  font-weight: ${FONT.FW_500};
  font-size: ${FONT.F_XS};
  color: ${COLORS.PRIMARY};
  padding: 4px 0;

  &:hover { opacity: 0.8; }
`

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLORS.GREY_T_90};
  margin: 12px 0;
`

export const SortableGlobalStyle = createGlobalStyle`
  .sortable-helper {
    z-index: 9999 !important;
    background: ${COLORS.WHITE} !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border-radius: 4px;
    opacity: 0.95;
    list-style: none;
  }
`
